import { useEffect } from 'react';
import { formatFieldValue, isEmptyValue, type FieldValue, type RowKind } from '../../lib/fieldValue';
import type { FieldCell } from '../../lib/rooms';
import type { Field } from '../../lib/templates';
import { useDialog } from '../../lib/useDialog';
import type { PresenceFocus } from '../../store/presenceStore';
import { DurationInput } from '../fields/DurationInput';
import { ImageField } from '../fields/ImageField';
import { LinkField } from '../fields/LinkField';
import { LongText } from '../fields/LongText';
import { NumberInput } from '../fields/NumberInput';
import { RatioInput } from '../fields/RatioInput';
import { ShortText } from '../fields/ShortText';
import { StarsPicker } from '../fields/StarsPicker';
import { TierPicker } from '../fields/TierPicker';

interface FieldEditorProps {
  roomId: number;
  field: Field;
  rowKind: RowKind;
  rowLabel: string;
  cell: FieldCell | undefined;
  disabled: boolean;
  isLocked: boolean;
  canToggleLock: boolean;
  isActiveReveal: boolean;
  canReveal: boolean;
  onCommit: (value: FieldValue | null) => void;
  onToggleLock: () => void;
  onReveal: () => void;
  onHideReveal: () => void;
  onFocusChange: (focus: PresenceFocus | null) => void;
  onClose: () => void;
}

/**
 * Centered dialog (a pragmatic stand-in for a true anchored popover, which would need per-field
 * positioning logic against each cell's place in the scoreboard-row layout rather than one fixed
 * center point) holding the type-specific editor for one cell, plus the host-only lock toggle and
 * reveal controls. `field.config`/`cell.value` are cast per `field.type` — the backend guarantees
 * they agree, the same assumption `formatFieldValue` callers already make.
 *
 * Opening a cell is always allowed for anyone who can view the room (see `RatingTable`'s cell
 * buttons, which stay enabled regardless of edit rights so they remain reachable by keyboard); this
 * component is what actually turns "can't edit" into a read-only view — the value plus a "Read-only"
 * eyebrow and none of the type-specific input controls, rather than a disabled version of them.
 */
export function FieldEditor({
  roomId,
  field,
  rowKind,
  rowLabel,
  cell,
  disabled,
  isLocked,
  canToggleLock,
  isActiveReveal,
  canReveal,
  onCommit,
  onToggleLock,
  onReveal,
  onHideReveal,
  onFocusChange,
  onClose,
}: FieldEditorProps) {
  const value = cell?.value ?? null;
  const isEmptyCell = isEmptyValue(value);
  const { dialogRef, onBackdropClick } = useDialog(onClose);

  // Presence focus: sent once on open, cleared once on close (see `docs/specs/WP5-realtime.md`).
  // `field.id`/`rowKind` are stable for the lifetime of one open editor — `RatingTable` always
  // fully unmounts this component before opening a different cell (see its own comment on why) —
  // so a mount/unmount effect is exactly "on open" / "on close" here, not a per-render resend.
  useEffect(() => {
    onFocusChange({ fieldId: field.id, rowKind });
    return () => onFocusChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally once per mount, see above
  }, []);

  return (
    <div
      ref={dialogRef}
      onClick={onBackdropClick}
      className="fixed inset-0 z-50 flex items-end justify-center bg-bg/80 p-4 md:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={`${field.label} · ${rowLabel}`}
      tabIndex={-1}
    >
      <div className="w-full max-w-md rounded-lg border border-border-strong bg-bg-elevated p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="eyebrow">{rowLabel}</span>
            <h2 className="display mt-1 text-2xl">{field.label}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-h-tap min-w-tap rounded-md border border-border-strong px-3 text-sm text-text-muted hover:border-accent-border hover:text-text"
          >
            Close
          </button>
        </div>

        {isLocked && <p className="mt-3 eyebrow text-signal">Locked</p>}
        {disabled && <p className="mt-3 eyebrow text-text-faint">Read-only</p>}

        <div className="mt-4">{disabled ? renderReadOnlyValue() : renderInput()}</div>

        {(canToggleLock || canReveal) && (
          <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
            {canToggleLock && (
              <button
                type="button"
                onClick={onToggleLock}
                className="min-h-tap rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted hover:border-accent-border hover:text-text"
              >
                {isLocked ? 'Unlock' : 'Lock'}
              </button>
            )}
            {canReveal &&
              (isActiveReveal ? (
                <button
                  type="button"
                  onClick={onHideReveal}
                  className="min-h-tap rounded-md border border-signal px-3 text-sm font-semibold text-signal"
                >
                  Hide reveal
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onReveal}
                  disabled={isEmptyCell}
                  className="min-h-tap rounded-md border border-accent-border bg-accent-dim px-3 text-sm font-semibold text-accent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Reveal
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );

  /** The read-only branch: the value as plain text (or "No value yet"), no interactive controls. */
  function renderReadOnlyValue() {
    if (isEmptyCell || value === null) {
      return <p className="text-sm text-text-faint">No value yet.</p>;
    }
    return <p className="text-lg text-text">{formatFieldValue(value, field.config)}</p>;
  }

  function renderInput() {
    // Switching on `field.config.type` (not `field.type`) lets TypeScript narrow `field.config`
    // itself — the two are always equal (the backend guarantees it), but `field` isn't typed as a
    // union discriminated on `type`, so only the nested `config.type` check actually narrows it.
    const config = field.config;
    switch (config.type) {
      case 'TIER':
        return <TierPicker field={field} config={config} value={value?.type === 'TIER' ? value : null} onCommit={onCommit} disabled={disabled} />;
      case 'STARS':
        return <StarsPicker field={field} config={config} value={value?.type === 'STARS' ? value : null} onCommit={onCommit} disabled={disabled} />;
      case 'DURATION':
        return <DurationInput field={field} value={value?.type === 'DURATION' ? value : null} onCommit={onCommit} disabled={disabled} />;
      case 'RATIO':
        return <RatioInput field={field} config={config} value={value?.type === 'RATIO' ? value : null} onCommit={onCommit} disabled={disabled} />;
      case 'NUMBER':
        return <NumberInput field={field} config={config} value={value?.type === 'NUMBER' ? value : null} onCommit={onCommit} disabled={disabled} />;
      case 'SHORT_TEXT':
        return <ShortText field={field} config={config} value={value?.type === 'SHORT_TEXT' ? value : null} onCommit={onCommit} disabled={disabled} />;
      case 'LONG_TEXT':
        return <LongText field={field} config={config} value={value?.type === 'LONG_TEXT' ? value : null} onCommit={onCommit} disabled={disabled} />;
      case 'LINK':
        return <LinkField field={field} value={value?.type === 'LINK' ? value : null} onCommit={onCommit} disabled={disabled} />;
      case 'IMAGE':
        return (
          <ImageField
            roomId={roomId}
            value={value?.type === 'IMAGE' ? value : null}
            onCommit={onCommit}
            disabled={disabled}
          />
        );
    }
  }
}
